import { MarketingHero } from '../components';
import { MerchantsNearbySection } from '../components/MerchantsNearbySection';
import ProductDiscoverySection from '../../product/components/ProductDiscoverySection';
import { featuredCategories } from '../home.data';
import type { ProductDiscoveryConfig } from '../home.types';
import { AdSlot, AD_SLOTS } from '../../ads';
import { CategoryBentoGrid } from '../components/CategoryBentoGrid';
import Newsletter from '../../static/components/Newsletter';
import { useProductExploreQuery } from '../../product/product.services';
import { mapApiProductToUI } from '../home.types';
import type { PaginatedProductResponse } from '../../product/product.types';
import SEO from '../../../components/SEO';

import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

function DynamicFeaturedSection({
  category,
  index
}: {
  category: typeof featuredCategories[0];
  index: number
}) {
  const [ref, inView] = useIntersectionObserver({ triggerOnce: true, rootMargin: '400px' });
  const { data, isLoading, isFetching } = useProductExploreQuery(
    { type: category.value as any },
    { enabled: inView }
  );

  const totalCount = data && !Array.isArray(data)
    ? (data as PaginatedProductResponse).total_product_count ?? (data as PaginatedProductResponse).results?.length ?? 0
    : 0;

  const products = data && !Array.isArray(data)
    ? (data as PaginatedProductResponse).results.map(mapApiProductToUI)
    : [];

  const config: ProductDiscoveryConfig = {
    title: category.label === 'Recommended' ? 'Recommended For You' :
      category.label === 'Mouth Watering' ? 'Mouth Watering Deals' :
        category.label === 'New Arrivals' ? 'Shop New Arrivals' :
          category.label,
    widgetType: category.value.replace(/-/g, '_'),
    defaultLayout: 'grid',
    viewAllLink: category.href,
  };

  const adSlotId = (AD_SLOTS as any)[`HP_BILLBOARD_${index + 1}`];

  // While it's fetching or hasn't fetched yet due to intersection, show loader
  const showLoading = isLoading || isFetching || (!inView && !data);

  if (!showLoading && products.length === 0) {
    return <div ref={ref as any} />;
  }

  return (
    <div className='mb-8 min-h-[400px]' ref={ref as any}>
      <ProductDiscoverySection
        products={products}
        config={config}
        isLoading={showLoading}
        totalCount={totalCount}
      />
      {adSlotId && (
        <AdSlot id={adSlotId as string} />
      )}
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Authentic Global Ingredients & Products"
        description="The marketplace for authentic global ingredients. Connecting diaspora communities across the UK with the authentic products they love from home. Shop traditional foods, spices, and essentials from independent global food merchants."
      />
      {/* Marketing Phase Hero */}
      <MarketingHero />

      {/* Hero Section - Dynamic carousel (Commented out for marketing phase)
      <AdSlot id={AD_SLOTS.HP_HERO} autoPlayInterval={6000} />
      */}

      <div className='px-5 lg:px-10'>
        {/* Bento Grid Section - Featured categories */}
        <CategoryBentoGrid />

        <MerchantsNearbySection />

        {featuredCategories.slice(0, 15).map((category, index) => (
          <DynamicFeaturedSection
            key={category.value}
            category={category}
            index={index}
          />
        ))}
      </div>


      {/* Newsletter Section - GDPR Compliant */}
      <Newsletter />
    </div>
  );
}

export default HomePage;
